import React, { useLayoutEffect, useRef } from 'react';
import { gsap, Linear } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import {
  HeroSectionFullWidthContainer,
  PairedText,
  SectionContainer,
  StyledGetStartedButton,
  TextPairingWrapper,
  VideoContainer,
} from './HeroSection.styles';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { Box } from '@components/Box';
import ReactHtmlParser from 'react-html-parser';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { getCreateWeddingUrl } from '@utils/urlHelpers.util';
import { useTranslation } from 'react-i18next';

export const HeroSection: React.FC = () => {
  const heroVideoRef = useRef<HTMLDivElement>(null);
  const sectionId = 'RSVP_Hero';
  const sectionTelemetryRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const { t } = useTranslation('onlineRsvp');
  const { title, content, getStartedBtn } = t('heroSection');

  const { animationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('wedding-invitations/hero/bodymovin/invites-rsvp_hero_data.json'), // Required
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'heroSectionAnimation',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: heroVideoRef, isLoadInitially: true },
    'aggressive'
  );

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let livestreamTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        livestreamTimeline = gsap.timeline({ paused: true });
        livestreamTimeline.to(
          { frame: 0 },
          1,
          {
            frame: 1,
            onUpdate: function () {
              animationRef.current?.goToAndStop(Math.round(this.progress() * 28), true);
            },
            ease: Linear.easeNone,
          },
          0
        );
      };
      const startScrollAnimation = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: `#${sectionId}`,
          duration: '190%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            livestreamTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      startScrollAnimation();
    }
    const clearScene = () => {
      if (livestreamTimeline) {
        livestreamTimeline.progress(0);
        livestreamTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, animationRef]);

  return (
    <SectionContainer id={sectionId} ref={sectionTelemetryRef}>
      <PairedText>
        <TextPairingWrapper
          alignment="center"
          title={ReactHtmlParser(title)}
          titleVariant="hed72Hero"
          titleColor="blue6"
          titleTagName="h2"
          body={content}
          bodyTagName="h1"
          bodyVariant="dek24"
          bodyColor="blue6"
        />
        <Box style={{ textAlign: 'center' }} paddingTop={[6]}>
          <StyledGetStartedButton id="heroBtn" href={getCreateWeddingUrl('online-rsvp')} forwardedAs={'a'} variant="primary">
            {getStartedBtn}
          </StyledGetStartedButton>
        </Box>
      </PairedText>
      <HeroSectionFullWidthContainer>
        <VideoContainer ref={heroVideoRef} />
      </HeroSectionFullWidthContainer>
    </SectionContainer>
  );
};
